import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider, useAuth } from "@/auth";
import { I18nProvider } from "@/i18n";
import { ThemeProvider } from "@/theme";

import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import CatalogPage from "@/pages/CatalogPage";
import ProductDetailPage from "@/pages/ProductDetailPage";
import ProductFormPage from "@/pages/ProductFormPage";
import ProblemsListPage from "@/pages/ProblemsListPage";
import ProblemDetailPage from "@/pages/ProblemDetailPage";
import ProblemFormPage from "@/pages/ProblemFormPage";
import ExpiriesListPage from "@/pages/ExpiriesListPage";
import ExpiryFormPage from "@/pages/ExpiryFormPage";
import ExpiryDetailPage from "@/pages/ExpiryDetailPage";
import HistoryPage from "@/pages/HistoryPage";
import ReservationsPage from "@/pages/ReservationsPage";
import ChatListPage from "@/pages/ChatListPage";
import ChatDetailPage from "@/pages/ChatDetailPage";
import CredentialsPage from "@/pages/CredentialsPage";
import NearExpiryPage from "@/pages/NearExpiryPage";
import InvoiceCheckListPage from "@/pages/InvoiceCheckListPage";
import InvoiceCheckDetailPage from "@/pages/InvoiceCheckDetailPage";
import GlobalSearchPage from "@/pages/GlobalSearchPage";
import ListinoSearchPage from "@/pages/ListinoSearchPage";
import ListiniManagerPage from "@/pages/ListiniManagerPage";
import { Onboarding } from "@/components/Onboarding";
import { OfflineBanner } from "@/components/OfflineBanner";
import { InstallAppBanner } from "@/components/InstallAppBanner";
import { UpdateBanner } from "@/components/UpdateBanner";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const Protected = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

const TitolareOnly = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (user.role !== "titolare") return <Navigate to="/" replace />;
  return children;
};

function App() {
  useEffect(() => {
    // Remove the HTML splash element as soon as React is mounted.
    // The bootstrap splash in public/index.html has z-index: 99999 which sits above
    // the Onboarding overlay. If the `load` event fires late (SW cached start,
    // slow cellular, iOS PWA) the splash can flash back into view during a repaint
    // when the user taps a button. Killing it immediately on React mount fixes that.
    const splash = document.getElementById("tf-splash");
    if (splash) {
      splash.classList.add("hide");
      setTimeout(() => splash.remove(), 250);
    }

    if ("serviceWorker" in navigator) {
      window.addEventListener("load", () => {
        navigator.serviceWorker.register("/sw.js").catch(() => null);
      });
    }
  }, []);

  return (
    <div className="App">
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-center" richColors closeButton />
            <Onboarding>
              <OfflineBanner />
              <InstallAppBanner />
              <UpdateBanner />
              <ErrorBoundary>
              <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<Protected><DashboardPage /></Protected>} />
              <Route path="/catalog/:storeId" element={<Protected><CatalogPage /></Protected>} />
              <Route path="/catalog/:storeId/near-expiry" element={<Protected><NearExpiryPage /></Protected>} />
              <Route path="/catalog/:storeId/invoice-check" element={<Protected><InvoiceCheckListPage /></Protected>} />
              <Route path="/catalog/:storeId/invoice-check/:invoiceId" element={<Protected><InvoiceCheckDetailPage /></Protected>} />
              <Route path="/product/new" element={<Protected><ProductFormPage /></Protected>} />
              <Route path="/product/:productId" element={<Protected><ProductDetailPage /></Protected>} />
              <Route path="/product/:productId/edit" element={<Protected><ProductFormPage /></Protected>} />
              <Route path="/problems" element={<Protected><ProblemsListPage /></Protected>} />
              <Route path="/problem/new" element={<Protected><ProblemFormPage /></Protected>} />
              <Route path="/problem/:problemId" element={<Protected><ProblemDetailPage /></Protected>} />
              <Route path="/problem/:problemId/edit" element={<Protected><ProblemFormPage /></Protected>} />
              <Route path="/expiries" element={<Protected><ExpiriesListPage /></Protected>} />
              <Route path="/expiry/new" element={<Protected><ExpiryFormPage /></Protected>} />
              <Route path="/expiry/:expiryId" element={<Protected><ExpiryDetailPage /></Protected>} />
              <Route path="/expiry/:expiryId/edit" element={<Protected><ExpiryFormPage /></Protected>} />
              <Route path="/history" element={<TitolareOnly><HistoryPage /></TitolareOnly>} />
              <Route path="/reservations" element={<Protected><ReservationsPage /></Protected>} />
              <Route path="/chat" element={<Protected><ChatListPage /></Protected>} />
              <Route path="/chat/:convId" element={<Protected><ChatDetailPage /></Protected>} />
              <Route path="/search" element={<Protected><GlobalSearchPage /></Protected>} />
              <Route path="/listino" element={<Protected><ListinoSearchPage /></Protected>} />
              <Route path="/listini" element={<TitolareOnly><ListiniManagerPage /></TitolareOnly>} />
              <Route path="/credentials" element={<TitolareOnly><CredentialsPage /></TitolareOnly>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </ErrorBoundary>
            </Onboarding>
          </BrowserRouter>
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
